import React, { useMemo } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Chart } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { GRAPH_PALETTE } from "../theme/style/graphChartPalette";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  annotationPlugin,
  ChartDataLabels
);

/**
 * LongTermNetZeroPathwayChart
 * 
 * Implements Graph 5: Greenhouse Gas Emissions Reduction Pathway (Scope 1, 2, and 3)
 * Dynamically computes trajectories, shaded buffer areas, reference lines, and callout badges
 * based on selected Base Year, MRY (Most Recent Year), Target Year, and Target Reduction %.
 */
function LongTermNetZeroPathwayChart({
  baseYear = 2024,
  baseYearEmissions = 4016.91,
  mostRecentYear = 2025,
  mostRecentYearEmissions = 3098.00,
  targetYear = 2050,
  targetReductionPercent = 0.90, // e.g., 0.90 for 90%
  unit = "tCO₂e",
  title,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const axisColors = isDark ? GRAPH_PALETTE.axes.darkMode : GRAPH_PALETTE.axes.lightMode;

  const yBase = parseInt(baseYear, 10);
  const yMry = parseInt(mostRecentYear, 10);
  const yTarget = parseInt(targetYear, 10);

  const eBase = Number(baseYearEmissions) || 0;
  const eMry = Number(mostRecentYearEmissions) || 0;
  const reductionPct = Number(targetReductionPercent) || 0.90;
  const eTarget = Number((eBase * (1 - reductionPct)).toFixed(2));

  // Generate continuous annual years from yBase to yTarget
  const years = useMemo(() => {
    if (!yBase || !yTarget || yBase >= yTarget) return [];
    const list = [];
    for (let yr = yBase; yr <= yTarget; yr++) {
      list.push(yr);
    }
    return list;
  }, [yBase, yTarget]);

  // Slopes and trajectories
  const trajectoryData = useMemo(() => {
    if (years.length === 0 || eBase <= 0) {
      return {
        years: [],
        requiredReduction: [],
        greenTargetPathway: [],
        actualBars: [],
        targetBar: [],
        pctReductionList: [],
      };
    }

    const totalYearsBaseToTarget = yTarget - yBase;
    const slopeBase = totalYearsBaseToTarget > 0 ? (eTarget - eBase) / totalYearsBaseToTarget : 0;

    const totalYearsMryToTarget = yTarget - yMry;
    const slopeMry = totalYearsMryToTarget > 0 ? (eTarget - eMry) / totalYearsMryToTarget : 0;

    const reqReduction = [];
    const greenPathway = [];
    const actualBars = [];
    const targetBar = [];
    const pctReductionList = [];

    years.forEach((yr) => {
      // 1. Required Reduction from Base
      const reqVal = Number((eBase + slopeBase * (yr - yBase)).toFixed(2));
      reqReduction.push(reqVal);

      // % Reduction from Base
      const pctRed = eBase > 0 ? Number((((eBase - reqVal) / eBase) * 100).toFixed(2)) : 0;
      pctReductionList.push(pctRed);

      // 2. Green Target Pathway (starts from MRY down to Target)
      if (yr < yMry) {
        greenPathway.push(null);
      } else {
        const greenVal = Number((eMry + slopeMry * (yr - yMry)).toFixed(2));
        greenPathway.push(greenVal);
      }

      // 3. Actual Emissions Bar (Base Year and MRY only)
      if (yr === yBase) {
        actualBars.push(eBase);
      } else if (yr === yMry) {
        actualBars.push(eMry);
      } else {
        actualBars.push(null);
      }

      // 4. Target Bar (Target Year only)
      if (yr === yTarget) {
        targetBar.push(eTarget);
      } else {
        targetBar.push(null);
      }
    });

    return {
      years,
      requiredReduction: reqReduction,
      greenTargetPathway: greenPathway,
      actualBars,
      targetBar,
      pctReductionList,
    };
  }, [years, yBase, yMry, yTarget, eBase, eMry, eTarget]);

  // Key Percentage Metrics
  const pctChangeActual = eBase > 0 ? ((eMry - eBase) / eBase) * 100 : 0;
  const pctChangeBaseToTarget = -(reductionPct * 100);
  const pctChangeMryToTarget = eMry > 0 ? ((eTarget - eMry) / eMry) * 100 : 0;

  // Max scale calculation
  const maxEmission = Math.max(eBase, eMry, 100);
  const yAxisMax = Math.ceil((maxEmission * 1.15) / 500) * 500;

  // Chart Data configuration
  const chartData = useMemo(() => {
    return {
      labels: trajectoryData.years.map(String),
      datasets: [
        // 1. Actual Emissions Bars
        {
          type: "bar",
          label: t("Actual Emissions"),
          data: trajectoryData.actualBars,
          backgroundColor: (ctx) => {
            const index = ctx.dataIndex;
            const yr = trajectoryData.years[index];
            return yr === yBase ? "#1e3a5f" : "#00a8e8";
          },
          borderColor: (ctx) => {
            const index = ctx.dataIndex;
            const yr = trajectoryData.years[index];
            return yr === yBase ? "#112233" : "#0088bb";
          },
          borderWidth: 1,
          barPercentage: 0.8,
          categoryPercentage: 0.7,
          order: 2,
          yAxisID: "y",
          datalabels: {
            display: (ctx) => ctx.dataset.data[ctx.dataIndex] !== null,
            color: isDark ? "#ffffff" : "#1e293b",
            anchor: "end",
            align: "top",
            offset: 4,
            font: {
              family: GRAPH_PALETTE.font.family,
              size: 11,
              weight: 600,
            },
            formatter: (val) => {
              if (val === null || val === undefined) return "";
              return Number(val).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
            },
          },
        },
        // 2. Target Bar
        {
          type: "bar",
          label: `${yTarget} ${t("Target Bar")}`,
          data: trajectoryData.targetBar,
          backgroundColor: "#166534",
          borderColor: "#14532d",
          borderWidth: 1,
          barPercentage: 0.8,
          categoryPercentage: 0.7,
          order: 2,
          yAxisID: "y",
          datalabels: {
            display: (ctx) => ctx.dataset.data[ctx.dataIndex] !== null,
            color: isDark ? "#ffffff" : "#1e293b",
            anchor: "end",
            align: "top",
            offset: 4,
            font: {
              family: GRAPH_PALETTE.font.family,
              size: 11,
              weight: 600,
            },
            formatter: (val) => {
              if (val === null || val === undefined) return "";
              return Number(val).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
            },
          },
        },
        // 3. Required Reduction from Base (solid orange line)
        {
          type: "line",
          label: t("Required Reduction from Base"),
          data: trajectoryData.requiredReduction,
          borderColor: "#ea580c",
          borderWidth: 2.5,
          pointRadius: 0,
          pointHoverRadius: 4,
          tension: 0,
          order: 1,
          yAxisID: "y",
          datalabels: {
            display: false,
          },
        },
        // 4. Green Target Pathway (dashed green line)
        {
          type: "line",
          label: t("Green Target Pathway"),
          data: trajectoryData.greenTargetPathway,
          borderColor: "#16a34a",
          borderWidth: 2.5,
          borderDash: [6, 5],
          pointRadius: 0,
          pointHoverRadius: 4,
          tension: 0,
          order: 1,
          yAxisID: "y",
          datalabels: {
            display: false,
          },
        },
        // 5. Orange Shaded Gap Area (between Required Reduction and Green Target Pathway)
        {
          type: "line",
          label: t("Orange Shaded Area"),
          data: trajectoryData.requiredReduction,
          fill: 5, // fill to Green Target Area
          backgroundColor: "rgba(251, 146, 60, 0.22)",
          borderColor: "transparent",
          pointRadius: 0,
          tension: 0,
          order: 4,
          yAxisID: "y",
          datalabels: {
            display: false,
          },
        },
        // 6. Green Target Area (under Green Target Pathway down to 0)
        {
          type: "line",
          label: t("Green Target Area"),
          data: trajectoryData.greenTargetPathway,
          fill: "origin",
          backgroundColor: "rgba(34, 197, 94, 0.24)",
          borderColor: "transparent",
          pointRadius: 0,
          tension: 0,
          order: 5,
          yAxisID: "y",
          datalabels: {
            display: false,
          },
        },
      ],
    };
  }, [trajectoryData, yBase, yMry, yTarget, isDark, t]);

  // Midpoint positions for annotations
  const xMryIndex = trajectoryData.years.indexOf(yMry);
  const xMidPointIndex = Math.floor(trajectoryData.years.length * 0.65);
  const xFarRightIndex = Math.floor(trajectoryData.years.length * 0.88);

  const options = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "index",
        intersect: false,
      },
      plugins: {
        datalabels: {
          display: false, // Globally OFF by default for lines
        },
        legend: {
          position: "bottom",
          labels: {
            font: {
              family: GRAPH_PALETTE.font.family,
              size: 11,
              weight: 500,
            },
            color: axisColors.tick,
            usePointStyle: true,
            boxWidth: 10,
            padding: 18,
          },
        },
        tooltip: {
          backgroundColor: isDark ? "rgba(15, 23, 42, 0.95)" : "rgba(255, 255, 255, 0.95)",
          titleColor: isDark ? "#ffffff" : "#1b1c1a",
          bodyColor: isDark ? "#cbd5e1" : "#4b5563",
          borderColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
          borderWidth: 1,
          padding: 12,
          bodyFont: { family: GRAPH_PALETTE.font.family, size: 12 },
          titleFont: { family: GRAPH_PALETTE.font.family, size: 13, weight: 600 },
          callbacks: {
            label: (context) => {
              const label = context.dataset.label || "";
              const val = context.parsed.y;
              if (val === null || val === undefined) return null;
              if (label.includes("Area")) return null; // keep tooltip clean
              return ` ${label}: ${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${unit}`;
            },
          },
        },
        annotation: {
          clip: false,
          annotations: {
            // Horizontal Line: Base Year Baseline
            baseYearLine: {
              type: "line",
              yMin: eBase,
              yMax: eBase,
              borderColor: "#ef4444",
              borderWidth: 2,
              borderDash: [6, 4],
              label: {
                display: true,
                content: `${t("Base Year")} ${yBase}`,
                position: "end",
                backgroundColor: "transparent",
                color: "#dc2626",
                font: {
                  family: GRAPH_PALETTE.font.family,
                  size: 12,
                  weight: "bold",
                },
                xAdjust: -8,
                yAdjust: -12,
              },
            },
            // Horizontal Line: MRY Reference
            mryLine: {
              type: "line",
              yMin: eMry,
              yMax: eMry,
              borderColor: "#0284c7",
              borderWidth: 2,
              borderDash: [6, 4],
              label: {
                display: true,
                content: `${t("MRY")} ${yMry}`,
                position: "end",
                backgroundColor: "transparent",
                color: "#0284c7",
                font: {
                  family: GRAPH_PALETTE.font.family,
                  size: 12,
                  weight: "bold",
                },
                xAdjust: -8,
                yAdjust: -12,
              },
            },
            // Badge 1: Actual change from Base Year to MRY
            badgeActualChange: {
              type: "label",
              xValue: Math.max(0, xMryIndex),
              yValue: Math.min(yAxisMax * 0.94, eBase * 1.08),
              backgroundColor: isDark ? "rgba(34, 197, 94, 0.2)" : "#dcfce7",
              borderColor: "#16a34a",
              borderWidth: 1.5,
              borderRadius: 6,
              content: [
                `${pctChangeActual >= 0 ? "+" : ""}${pctChangeActual.toFixed(2)}%`,
                `${yBase} to ${yMry}`,
              ],
              font: {
                family: GRAPH_PALETTE.font.family,
                size: 11,
                weight: "bold",
              },
              color: isDark ? "#4ade80" : "#15803d",
              padding: { top: 6, bottom: 6, left: 10, right: 10 },
            },
            // Badge 2: Base to Target Year (-90%)
            badgeBaseToTarget: {
              type: "label",
              xValue: Math.min(trajectoryData.years.length - 1, xFarRightIndex),
              yValue: (eBase + eTarget) / 2,
              xAdjust: -62, // Placed cleanly to the left of the arrow to eliminate overlap
              backgroundColor: isDark ? "rgba(239, 68, 68, 0.2)" : "#fee2e2",
              borderColor: "#dc2626",
              borderWidth: 1.5,
              borderRadius: 6,
              content: [
                `${pctChangeBaseToTarget.toFixed(2)}%`,
                `${yBase} to ${yTarget}`,
              ],
              font: {
                family: GRAPH_PALETTE.font.family,
                size: 11,
                weight: "bold",
              },
              color: isDark ? "#f87171" : "#b91c1c",
              padding: { top: 6, bottom: 6, left: 10, right: 10 },
            },
            // Vertical Arrow for Base to Target
            arrowBaseToTarget: {
              type: "line",
              xMin: Math.min(trajectoryData.years.length - 1, xFarRightIndex),
              xMax: Math.min(trajectoryData.years.length - 1, xFarRightIndex),
              yMin: eTarget + 20,
              yMax: eBase - 20,
              borderColor: "#dc2626",
              borderWidth: 2,
              arrowHeads: {
                start: { display: true, fill: true, length: 7, width: 5 },
                end: { display: true, fill: true, length: 7, width: 5 },
              },
            },
            // Badge 3: MRY to Target Year (-87.03%)
            badgeMryToTarget: {
              type: "label",
              xValue: Math.min(trajectoryData.years.length - 1, xMidPointIndex),
              yValue: (eMry + eTarget) / 2,
              xAdjust: 62, // Placed cleanly to the right of the arrow to eliminate overlap
              backgroundColor: isDark ? "rgba(2, 132, 199, 0.2)" : "#e0f2fe",
              borderColor: "#0284c7",
              borderWidth: 1.5,
              borderRadius: 6,
              content: [
                `${pctChangeMryToTarget.toFixed(2)}%`,
                `${yMry} to ${yTarget}`,
              ],
              font: {
                family: GRAPH_PALETTE.font.family,
                size: 11,
                weight: "bold",
              },
              color: isDark ? "#38bdf8" : "#0369a1",
              padding: { top: 6, bottom: 6, left: 10, right: 10 },
            },
            // Vertical Arrow for MRY to Target
            arrowMryToTarget: {
              type: "line",
              xMin: Math.min(trajectoryData.years.length - 1, xMidPointIndex),
              xMax: Math.min(trajectoryData.years.length - 1, xMidPointIndex),
              yMin: eTarget + 20,
              yMax: eMry - 20,
              borderColor: "#0284c7",
              borderWidth: 2,
              arrowHeads: {
                start: { display: true, fill: true, length: 7, width: 5 },
                end: { display: true, fill: true, length: 7, width: 5 },
              },
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            color: isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.06)",
          },
          ticks: {
            color: axisColors.tick,
            font: {
              family: GRAPH_PALETTE.font.family,
              size: 11,
              weight: 500,
            },
            callback: function (val, index) {
              const yr = trajectoryData.years[index];
              // Show baseYear, mry, targetYear, and every 5 years
              if (
                yr === yBase ||
                yr === yMry ||
                yr === yTarget ||
                yr % 5 === 0
              ) {
                return yr;
              }
              return "";
            },
          },
          title: {
            display: true,
            text: t("Year"),
            color: axisColors.tick,
            font: {
              family: GRAPH_PALETTE.font.family,
              size: 12,
              weight: 600,
            },
          },
        },
        y: {
          type: "linear",
          position: "left",
          min: 0,
          max: yAxisMax,
          grid: {
            color: isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.06)",
          },
          ticks: {
            color: axisColors.tick,
            font: {
              family: GRAPH_PALETTE.font.family,
              size: 11,
              weight: 500,
            },
            callback: (val) => Number(val).toLocaleString(),
          },
          title: {
            display: true,
            text: `${t("Emissions")} (${unit})`,
            color: axisColors.tick,
            font: {
              family: GRAPH_PALETTE.font.family,
              size: 12,
              weight: 600,
            },
          },
        },
        y1: {
          type: "linear",
          position: "right",
          min: 0,
          max: 100,
          grid: {
            drawOnChartArea: false, // Prevents double grid lines
          },
          ticks: {
            color: axisColors.tick,
            font: {
              family: GRAPH_PALETTE.font.family,
              size: 11,
              weight: 500,
            },
            callback: (val) => `${val.toFixed(2)}%`,
            stepSize: 10,
          },
          title: {
            display: true,
            text: `% ${t("Reduction from Base Year")}`,
            color: axisColors.tick,
            font: {
              family: GRAPH_PALETTE.font.family,
              size: 12,
              weight: 600,
            },
          },
        },
      },
    };
  }, [
    trajectoryData,
    eBase,
    eMry,
    eTarget,
    yBase,
    yMry,
    yTarget,
    yAxisMax,
    pctChangeActual,
    pctChangeBaseToTarget,
    pctChangeMryToTarget,
    xMryIndex,
    xMidPointIndex,
    xFarRightIndex,
    isDark,
    axisColors,
    unit,
    t,
  ]);

  if (!years || years.length === 0 || eBase <= 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 320,
          p: 3,
          borderRadius: "12px",
          bgcolor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
          border: `1px dashed ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"}`,
        }}
      >
        <Typography variant="body1" sx={{ color: "text.secondary", fontWeight: 500 }}>
          {t("Please specify Base Year, Target Year, and ensure emissions are populated to view the pathway.")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 600,
          color: "text.primary",
          textAlign: "center",
          fontSize: "15px",
        }}
      >
        {title || `${t("Greenhouse Gas Emissions Reduction Pathway (Scope 1, 2 and 3)")} (${yBase}–${yTarget})`}
      </Typography>

      <div style={{ width: "100%", height: 500, position: "relative" }}>
        <Chart type="bar" data={chartData} options={options} />
      </div>
    </Box>
  );
}

export default LongTermNetZeroPathwayChart;
